// Globals
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  activateUser,
  deactivateUser,
  getUserDetails,
  getUsersList,
  updateUserDetails,
} from '../../feature/actionCreators';
import { usersSelector } from '../../feature/selectors';

// Components
import { IFormInstance, showSuccessToast } from '@/components';
import { Skeleton } from '@/ui';
import { CreateUserForm } from '../../components';
import { StyledPageWrapper } from './CreateUserContainerStyles';

// Models
import { UserStatus } from '@/models/users.model';

// Helpers
import { TUserFormValues } from '../../helpers/types';
import { UserDetailsDTO } from '../../dtos';

interface ICreateUserContainer {
  form: IFormInstance;
  id?: string | null;
  onFormSubmit: () => void;
}

const CreateUserContainer = ({ form, id, onFormSubmit }: ICreateUserContainer) => {
  const dispatch = useAppDispatch();
  const { isLoading, userDetails } = useAppState(usersSelector);
  const { t } = useTranslation(['users']);

  const handleSubmit = async (values: TUserFormValues) => {
    const userDetailsDto = new UserDetailsDTO(values, id);
    await dispatch(updateUserDetails({ ...userDetailsDto })).unwrap();
    onFormSubmit();
    await dispatch(getUsersList({})).unwrap();
    showSuccessToast({ message: t('user_data_saved') });
  };

  const getInitialData = useCallback(async (userId?: string | null) => {
    if (userId) {
      const userData = await dispatch(getUserDetails(userId)).unwrap();
      form.setFieldsValue(userData);
    }
  }, []);

  const onUserStatusUpdate = (userId: string, status: UserStatus) => {
    const statusUpdateAction = status === UserStatus.ACTIVE ? activateUser : deactivateUser;
    dispatch(statusUpdateAction(userId));
  };

  useEffect(() => {
    void getInitialData(id);
  }, [id]);

  if (isLoading) return <Skeleton active />;

  return (
    <StyledPageWrapper>
      <CreateUserForm
        handleSubmit={handleSubmit}
        isLoading={isLoading}
        form={form}
        userDetails={userDetails}
        updateUserStatus={onUserStatusUpdate}
      />
    </StyledPageWrapper>
  );
};

export default CreateUserContainer;
